// Demo scenes. Every scene shows the real thing: an excerpt of our first film
// (Europe Has a Problem), the voiceover line it sits on, and the clips that
// were placed under that line, counted from the sequence itself.
// The film is in Bulgarian; the lines here are translated.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { CRWIcon, type IconName } from './icons';
import { LoopVideo, PanelFrame, PrTile } from './primitives';
import { GROUP_COLOR, TimelineSlice, type Group } from './timeline';

const FILM = 'Europe Has a Problem';

export interface Line { at: number; text: string }
export interface Row { label: string; n: number; g: Group }
export interface Shot {
  src: string;
  poster: string;
  tc: string; // where it sits in the film
  layer: string;
  icon: IconName;
  total: number; // clips under this line, all tracks
  lines: Line[];
  rows: Row[];
}

// Clip counts come from the Premiere sequence (see components/timeline.tsx):
// the clips on every track that overlap the excerpt's window.
export const SHOTS = {
  dresden: {
    src: '/video/footage-dresden.mp4', poster: '/video/footage-dresden.jpg', tc: '00:00:02', layer: 'Footage', icon: 'film', total: 18,
    lines: [{ at: 0, text: 'On the edge of the city’s biggest park stands a building' }, { at: 2.7, text: 'made almost entirely of glass.' }, { at: 4.9, text: 'And it is a car factory.' }],
    rows: [{ label: 'Picture', n: 3, g: 'ft' }, { label: 'Graphics', n: 1, g: 'gfx' }, { label: 'Sound', n: 8, g: 'sfx' }, { label: 'Subtitles', n: 3, g: 'sub' }],
  },
  y2003: {
    src: '/video/ai-2003.mp4', poster: '/video/ai-2003.jpg', tc: '00:10:24', layer: 'AI scene', icon: 'sparkle', total: 15,
    lines: [{ at: 0, text: 'America, 2003: Ford, Pfizer, General Motors.' }, { at: 3.1, text: 'Cars and medicine.' }],
    rows: [{ label: 'AI scene', n: 1, g: 'ai' }, { label: 'Picture', n: 5, g: 'ft' }, { label: 'Sound', n: 3, g: 'sfx' }, { label: 'Subtitles', n: 2, g: 'sub' }],
  },
  chart: {
    src: '/video/chart-euro.mp4', poster: '/video/chart-euro.jpg', tc: '00:41:01', layer: 'Graphic', icon: 'grid', total: 11,
    lines: [{ at: 0, text: 'In 2022 it fell below one, for the first time in 20 years.' }, { at: 3.8, text: 'Almost 40% cheaper.' }],
    rows: [{ label: 'Graphic', n: 1, g: 'gfx' }, { label: 'Picture', n: 2, g: 'ft' }, { label: 'Sound', n: 3, g: 'sfx' }, { label: 'Subtitles', n: 2, g: 'sub' }],
  },
  map: {
    src: '/video/map-gas.mp4', poster: '/video/map-gas.jpg', tc: '00:30:08', layer: 'Map', icon: 'globe', total: 15,
    lines: [{ at: 0, text: 'Before the war, about 40% of the European Union’s gas,' }, { at: 2.3, text: 'over 150 billion cubic metres a year, came from Russia.' }],
    rows: [{ label: 'Map', n: 1, g: 'gfx' }, { label: 'Picture', n: 3, g: 'ft' }, { label: 'Sound', n: 5, g: 'sfx' }, { label: 'Subtitles', n: 2, g: 'sub' }],
  },
  factory: {
    src: '/video/footage-factory.mp4', poster: '/video/footage-factory.jpg', tc: '00:00:08', layer: 'Footage', icon: 'film', total: 12,
    lines: [{ at: 0, text: 'Volkswagen calls it “the Glass Manufactory”' }, { at: 3.2, text: 'and builds it as a showcase.' }],
    rows: [{ label: 'Footage', n: 2, g: 'ft' }, { label: 'Graphics', n: 1, g: 'gfx' }, { label: 'Sound', n: 4, g: 'sfx' }, { label: 'Subtitles', n: 2, g: 'sub' }],
  },
  interview: {
    src: '/video/interview-tv.mp4', poster: '/video/interview-tv.jpg', tc: '00:15:41', layer: 'Interview', icon: 'mic', total: 8,
    lines: [{ at: 0, text: '“There is not a single person in the world who could make this pencil.”' }],
    rows: [{ label: 'Interview', n: 1, g: 'ji' }, { label: 'Picture', n: 1, g: 'ft' }, { label: 'Name card', n: 1, g: 'gfx' }, { label: 'Sound', n: 2, g: 'sfx' }],
  },
  gears: {
    src: '/video/ai-gears.mp4', poster: '/video/ai-gears.jpg', tc: '00:06:25', layer: 'AI scene', icon: 'sparkle', total: 18,
    lines: [{ at: 0, text: 'And from the 70s on, cheap gas from the USSR.' }, { at: 3.3, text: 'The fuel.' }],
    rows: [{ label: 'AI scene', n: 1, g: 'ai' }, { label: 'Picture', n: 4, g: 'ft' }, { label: 'Sound', n: 6, g: 'sfx' }, { label: 'Subtitles', n: 2, g: 'sub' }],
  },
  table: {
    src: '/video/graphic-table.mp4', poster: '/video/graphic-table.jpg', tc: '00:48:20', layer: 'Graphic', icon: 'grid', total: 13,
    lines: [{ at: 0, text: 'The benefits come fifteen years later, to someone else.' }, { at: 3.4, text: 'Stagnation: tomorrow, nothing happens.' }, { at: 5.3, text: 'The day after, the same.' }],
    rows: [{ label: 'Graphic', n: 1, g: 'gfx' }, { label: 'Picture', n: 2, g: 'ft' }, { label: 'Sound', n: 3, g: 'sfx' }, { label: 'Subtitles', n: 3, g: 'sub' }],
  },
  valve: {
    src: '/video/sound-valve.mp4', poster: '/video/sound-valve.jpg', tc: '00:39:03', layer: 'Sound', icon: 'sliders', total: 15,
    lines: [],
    rows: [{ label: 'Voice', n: 1, g: 'vo' }, { label: 'Effects', n: 1, g: 'sfx' }, { label: 'Music', n: 1, g: 'mus' }, { label: 'Picture', n: 7, g: 'ai' }],
  },
  subs: {
    src: '/video/subs-200k.mp4', poster: '/video/subs-200k.jpg', tc: '00:00:43', layer: 'Subtitles', icon: 'text', total: 19,
    lines: [],
    rows: [{ label: 'Subtitles', n: 2, g: 'sub' }, { label: 'Picture', n: 4, g: 'ft' }, { label: 'Graphics', n: 1, g: 'gfx' }, { label: 'Sound', n: 9, g: 'sfx' }],
  },
} satisfies Record<string, Shot>;

// The translated line that is being spoken at time t of the excerpt.
export function lineAt(lines: Line[], t: number) {
  let cur = '';
  for (const l of lines) if (t + 0.05 >= l.at) cur = l.text;
  return cur;
}

// The clips under the line, one row per layer, drawn as the blocks they are.
export function LayerRows({ rows, delay = 900 }: { rows: Row[]; delay?: number }) {
  return (
    <div className="ons-rows">
      {rows.map((r, i) => (
        <div key={r.label} className="ons-row">
          <span className="ons-row-name">{r.label}</span>
          <span className="ons-row-blocks">
            {Array.from({ length: r.n }, (_, k) => (
              <i key={k} className="crw-pop" style={{ animationDelay: `${delay + i * 130 + k * 55}ms`, background: GROUP_COLOR[r.g], flex: i === 0 ? `${3 - Math.min(k, 2) * 0.4} 1 0` : `${1 + ((k * 7 + i * 3) % 4) * 0.5} 1 0` }}></i>
            ))}
          </span>
          <span className="ons-row-n">{r.n}</span>
        </div>
      ))}
    </div>
  );
}

function LayerPill({ icon, label }: { icon: IconName; label: string }) {
  return <span className="ons-pill"><CRWIcon d={icon} size={11} />{label}</span>;
}

// One excerpt in the window chrome: the layer it shows, the picture with its
// translated line, and the clips placed under that line.
function FilmScene({ shot, extra }: { shot: Shot; extra?: ReactNode }) {
  const [t, setT] = useState(0);
  const line = lineAt(shot.lines, t);
  return (
    <PanelFrame title={FILM} badge={`TC ${shot.tc}`} pad={14}>
      <div className="ons-head">
        <LayerPill icon={shot.icon} label={shot.layer} />
        <span className="ons-head-n crw-fade" style={{ animationDelay: '700ms' }}><b>{shot.total}</b> clips under this line</span>
      </div>
      <div className="ons-screen">
        <LoopVideo src={shot.src} poster={shot.poster} onTime={setT} />
        {line ? <span key={line} className="ons-sub crw-fade">{line}</span> : null}
        {extra}
      </div>
      <LayerRows rows={shot.rows} />
    </PanelFrame>
  );
}

const SceneFootage = () => <FilmScene shot={SHOTS.factory} />;
const SceneInterview = () => <FilmScene shot={SHOTS.interview} />;
const SceneAi = () => <FilmScene shot={SHOTS.gears} />;
const SceneGraphic = () => <FilmScene shot={SHOTS.table} />;
const SceneSubs = () => <FilmScene shot={SHOTS.subs} extra={<span className="ons-tag">Original subtitles, in Bulgarian</span>} />;

// The sound scene plays with the film's own audio, once the visitor asks.
function SceneSound() {
  const shot = SHOTS.valve;
  const ref = useRef<HTMLVideoElement | null>(null);
  const [on, setOn] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.muted = true;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    void el.play().catch(() => {});
  }, []);
  const toggle = () => {
    const el = ref.current;
    if (!el) return;
    const next = !on;
    el.muted = !next;
    if (next) { el.currentTime = 0; void el.play().catch(() => {}); }
    setOn(next);
  };
  return (
    <PanelFrame title={FILM} badge={`TC ${shot.tc}`} pad={14}>
      <div className="ons-head">
        <LayerPill icon={shot.icon} label="Sound and music" />
        <span className="ons-head-n crw-fade" style={{ animationDelay: '700ms' }}>Voice, effects and music, on separate tracks</span>
      </div>
      <div className="ons-screen">
        <video ref={ref} src={shot.src} poster={shot.poster} muted loop playsInline preload="metadata" style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover', background: '#000' }} />
        <button className={`ons-soundbtn${on ? ' ons-soundbtn-on' : ''}`} onClick={toggle} aria-pressed={on}>
          <span className="ons-eq" aria-hidden="true"><i></i><i></i><i></i><i></i></span>
          {on ? 'Sound on' : 'Play with sound'}
        </button>
      </div>
      <LayerRows rows={shot.rows} />
    </PanelFrame>
  );
}

/* ================= HOW IT WORKS ================= */

// 01: what you send. Three files land, then the voice is timed word by word.
function SceneSend() {
  const files: { icon: IconName; name: string; meta: string }[] = [
    { icon: 'mic', name: 'voiceover.wav', meta: 'your recorded voice' },
    { icon: 'text', name: 'script.docx', meta: 'the script you read' },
    { icon: 'folder', name: 'style pack', meta: 'template · fonts · lower thirds · music rules' },
  ];
  const words = ['America,', '2003:', 'Ford,', 'Pfizer,', 'General', 'Motors.'];
  return (
    <PanelFrame title="New film" badge="Step 01" pad={18}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
        {files.map((f, i) => (
          <div key={f.name} className="onh-file crw-pop" style={{ animationDelay: `${300 + i * 420}ms` }}>
            <span className="onh-file-ic"><CRWIcon d={f.icon} size={14} /></span>
            <span style={{ minWidth: 0 }}>
              <b>{f.name}</b>
              <i>{f.meta}</i>
            </span>
            <span className="onh-file-ok crw-fade" style={{ animationDelay: `${700 + i * 420}ms` }}><CRWIcon d="check" size={11} sw={2.6} /></span>
          </div>
        ))}
      </div>
      <div className="onh-spine crw-fade" style={{ animationDelay: '1900ms' }}>
        <span className="onh-spine-label">Every word gets a timestamp</span>
        <div className="onh-wave" aria-hidden="true">
          {Array.from({ length: 56 }, (_, i) => <i key={i} style={{ height: `${22 + Math.abs(Math.sin(i * 1.7) * 62) + (i % 5) * 3}%` }}></i>)}
        </div>
        <div className="onh-words">
          {words.map((w, i) => (
            <span key={w} className="crw-pop" style={{ animationDelay: `${2300 + i * 190}ms` }}>
              <b>{w}</b>
            </span>
          ))}
        </div>
        <span className="onh-foot-quiet crw-fade" style={{ animationDelay: '3500ms' }}>00:10:24.5 → 00:10:27.6 · a real line from our first film</span>
      </div>
    </PanelFrame>
  );
}

// 02: you approve the look. Stills arrive, you tick them; nothing moves before that.
function SceneLook() {
  const stills = [1, 2, 3, 4, 5, 6];
  return (
    <PanelFrame title="Stills for approval" badge="Step 02" pad={16}>
      <div className="onh-stills">
        {stills.map((n, i) => {
          const note = i === 4;
          return (
            <div key={n} className="onh-still crw-pop" style={{ animationDelay: `${250 + i * 130}ms` }}>
              <img src={`/img/still-${n}.jpg`} alt="" loading="lazy" decoding="async" />
              <span className={`onh-still-mark crw-pop${note ? ' onh-still-note' : ''}`} style={{ animationDelay: `${1500 + i * 260}ms` }}>
                <CRWIcon d={note ? 'pen' : 'check'} size={10} sw={2.6} />
              </span>
            </div>
          );
        })}
      </div>
      <div className="onh-foot crw-fade" style={{ animationDelay: '3200ms' }}>
        <span><b>5</b> approved</span>
        <span><b>1</b> sent back with a note</span>
        <span style={{ flex: 1 }}></span>
        <span className="onh-foot-quiet">Nothing is animated before your yes</span>
      </div>
    </PanelFrame>
  );
}

// 03: you watch the cut and send notes against the timecode.
function SceneCut() {
  const notes = [
    { tc: '04:12', text: 'Hold this shot a little longer.' },
    { tc: '17:40', text: 'Music lower under the quote.' },
    { tc: '31:05', text: 'Swap this map for the second one.' },
  ];
  return (
    <PanelFrame title="Review cut" badge="Step 03" pad={16}>
      <div className="onh-cut">
        <div className="onh-cut-screen">
          <LoopVideo src="/video/film-europe.mp4" poster="/video/film-europe.jpg" />
          <span className="onh-cut-bar"><i></i></span>
        </div>
        <div className="onh-notes">
          <span className="onh-spine-label">Your notes</span>
          {notes.map((n, i) => (
            <div key={n.tc} className="onh-note crw-pop" style={{ animationDelay: `${900 + i * 700}ms` }}>
              <i>{n.tc}</i>
              <span>{n.text}</span>
            </div>
          ))}
          <span className="onh-sent crw-fade" style={{ animationDelay: '3300ms' }}><CRWIcon d="check" size={11} sw={2.6} /> 3 notes sent</span>
        </div>
      </div>
    </PanelFrame>
  );
}

// 04: what comes back. The project with its bins, and the master.
function SceneDeliver() {
  const bins: [string, string][] = [['Footage', '957'], ['Interviews', '122'], ['AI scenes', '245'], ['Graphics', '1,252'], ['Sound and music', '1,341'], ['Subtitles', '969']];
  return (
    <PanelFrame title="Delivered" badge="Step 04" pad={16}>
      <div className="onh-deliver">
        <div className="onh-tree">
          <div className="onh-tree-top crw-pop" style={{ animationDelay: '300ms' }}>
            <PrTile size={22} fs={10} />
            <b>Europe Has a Problem.prproj</b>
          </div>
          {bins.map(([name, n], i) => (
            <div key={name} className="onh-bin crw-pop" style={{ animationDelay: `${650 + i * 140}ms` }}>
              <CRWIcon d="folder" size={12} />
              <span>{name}</span>
              <i>{n}</i>
            </div>
          ))}
          <div className="onh-tree-top onh-master crw-pop" style={{ animationDelay: '1700ms' }}>
            <span className="onh-file-ic"><CRWIcon d="film" size={13} /></span>
            <b>Europe Has a Problem.mp4</b>
            <i>master · 50 min</i>
          </div>
        </div>
        <div className="onh-seq crw-fade" style={{ animationDelay: '1200ms' }}>
          <span className="onh-spine-label">The sequence, as delivered</span>
          <div className="onh-seq-canvas"><TimelineSlice from={600} to={760} /></div>
          <span className="onh-foot-quiet">30 tracks · open it and change anything</span>
        </div>
      </div>
    </PanelFrame>
  );
}

export const SCENES = {
  footage: SceneFootage,
  interview: SceneInterview,
  ai: SceneAi,
  graphic: SceneGraphic,
  sound: SceneSound,
  subs: SceneSubs,
  send: SceneSend,
  look: SceneLook,
  cut: SceneCut,
  deliver: SceneDeliver,
} as const;

export type SceneName = keyof typeof SCENES;
